import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import {
  policyTypeOptions,
  createDiscountPolicyId,
  type DiscountPolicyFormValue,
  type DiscountReasonCodeFormValue,
} from "./discountPolicies.helpers";

type DiscountPoliciesEditorProps = {
  value: DiscountPolicyFormValue[];
  onChange: (next: DiscountPolicyFormValue[]) => void;
};

const ensureNumber = (input: string, fallback: number, clamp?: { min?: number; max?: number }) => {
  const parsed = Number.parseInt(input, 10);
  if (Number.isNaN(parsed)) {
    return fallback;
  }
  const min = clamp?.min ?? Number.MIN_SAFE_INTEGER;
  const max = clamp?.max ?? Number.MAX_SAFE_INTEGER;
  return Math.min(Math.max(parsed, min), max);
};

const ReasonCodesEditor = ({
  policyIndex,
  items,
  onChange,
}: {
  policyIndex: number;
  items: DiscountReasonCodeFormValue[];
  onChange: (next: DiscountReasonCodeFormValue[]) => void;
}) => (
  <div className="space-y-3 rounded-xl border border-border/60 bg-muted/20 p-4">
    <div className="flex items-center justify-between gap-2">
      <h4 className="text-sm font-semibold text-foreground">Reason codes</h4>
      <Button
        size="sm"
        variant="outline"
        onClick={() =>
          onChange([
            ...items,
            {
              code: `reason_${items.length + 1}`,
              label: "New reason",
              requiresNote: false,
            },
          ])
        }
      >
        Add reason
      </Button>
    </div>
    {items.length === 0 ? (
      <p className="text-xs text-muted-foreground">
        No reason codes defined. Approvers can still use free-form notes when declining.
      </p>
    ) : (
      <div className="space-y-3">
        {items.map((reason, index) => (
          <div key={`${policyIndex}-reason-${index}`} className="rounded-lg border border-border/60 bg-background p-3">
            <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto] md:items-end">
              <div>
                <label className="text-xs font-medium uppercase text-muted-foreground">Code</label>
                <Input
                  value={reason.code}
                  placeholder="e.g. manager_approval"
                  onChange={(event) => {
                    const next = [...items];
                    next[index] = { ...reason, code: event.target.value };
                    onChange(next);
                  }}
                />
              </div>
              <div>
                <label className="text-xs font-medium uppercase text-muted-foreground">Label</label>
                <Input
                  value={reason.label}
                  placeholder="Visible label"
                  onChange={(event) => {
                    const next = [...items];
                    next[index] = { ...reason, label: event.target.value };
                    onChange(next);
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id={`reason-note-${policyIndex}-${index}`}
                  checked={reason.requiresNote}
                  onCheckedChange={(checked) => {
                    const next = [...items];
                    next[index] = { ...reason, requiresNote: checked };
                    onChange(next);
                  }}
                />
                <label
                  htmlFor={`reason-note-${policyIndex}-${index}`}
                  className="text-xs text-muted-foreground"
                >
                  Note required
                </label>
              </div>
            </div>
            <div className="mt-2 flex justify-end">
              <Button
                size="xs"
                variant="ghost"
                className="text-destructive"
                onClick={() => onChange(items.filter((_, reasonIdx) => reasonIdx !== index))}
              >
                Remove
              </Button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const DiscountPoliciesEditor = ({ value, onChange }: DiscountPoliciesEditorProps) => {
  const policyCount = value.length;

  const preview = useMemo(() => JSON.stringify(value, null, 2), [value]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-foreground">Discount policies</h3>
          <p className="text-sm text-muted-foreground">
            Configure guardrails for applying discounts. Policies sync with approval workflows.
          </p>
        </div>
        <Button
              onClick={() => {
                const now = Date.now();
                onChange([
                  ...value,
                  {
                    id: createDiscountPolicyId(),
                name: `New policy ${policyCount + 1}`,
                code: `policy_${policyCount + 1}`,
                type: "percentage",
                value: 10,
                maxStack: null,
                stackGroup: null,
                allowStacking: false,
                approvalRequired: false,
                reasonCodes: [],
                createdAt: now,
                updatedAt: now,
              },
            ]);
          }}
        >
          Add policy
        </Button>
      </div>

      {policyCount === 0 ? (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle>No discount policies</CardTitle>
            <CardDescription>
              Discounts will apply without enforcement. Add a policy to enable approval workflows.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        value.map((policy, index) => {
          const handleUpdate = (next: Partial<DiscountPolicyFormValue>) => {
            const nextPolicies = value.map((item, idx) =>
              idx === index
                ? {
                    ...item,
                    ...next,
                    updatedAt: Date.now(),
                  }
                : item,
            );
            onChange(nextPolicies);
          };

          const handleReasonCodesChange = (nextReasonCodes: DiscountReasonCodeFormValue[]) => {
            handleUpdate({ reasonCodes: nextReasonCodes });
          };

          return (
            <Card key={policy.id} className="border-border/60 shadow-sm">
              <CardHeader className="flex flex-col gap-2 border-b border-border/60 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-lg">{policy.name}</CardTitle>
                  <CardDescription>Code: {policy.code}</CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="secondary">{policy.type}</Badge>
                  <Badge variant="outline">{policy.approvalRequired ? "Approval required" : "Auto approve"}</Badge>
                  <span>Updated {new Date(policy.updatedAt).toLocaleString()}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 p-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase text-muted-foreground">Name</label>
                    <Input
                      value={policy.name}
                      onChange={(event) => handleUpdate({ name: event.target.value })}
                      placeholder="Policy display name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase text-muted-foreground">Code</label>
                    <Input
                      value={policy.code}
                      onChange={(event) => handleUpdate({ code: event.target.value })}
                      placeholder="Unique identifier"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase text-muted-foreground">Type</label>
                    <Select
                      value={policy.type}
                      onValueChange={(next) => handleUpdate({ type: next as DiscountPolicyFormValue["type"] })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {policyTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase text-muted-foreground">
                      {policy.type === "percentage" ? "Percentage value (bps)" : "Amount (cents)"}
                    </label>
                    <Input
                      inputMode="numeric"
                      value={policy.value.toString()}
                      onChange={(event) =>
                        handleUpdate({
                          value: ensureNumber(
                            event.target.value,
                            policy.value,
                            policy.type === "percentage" ? { min: 0, max: 10_000 } : { min: 0, max: 100_000 },
                          ),
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase text-muted-foreground">Max stack</label>
                    <Input
                      inputMode="numeric"
                      placeholder="optional"
                      value={policy.maxStack === null ? "" : policy.maxStack.toString()}
                      onChange={(event) =>
                        handleUpdate({
                          maxStack:
                            event.target.value.trim().length === 0
                              ? null
                              : ensureNumber(event.target.value, policy.maxStack ?? 1, { min: 1, max: 5 }),
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold uppercase text-muted-foreground">
                      Stack group (optional)
                    </label>
                    <Input
                      value={policy.stackGroup ?? ""}
                      placeholder="e.g. dine-in"
                      onChange={(event) =>
                        handleUpdate({
                          stackGroup: event.target.value.trim().length > 0 ? event.target.value : null,
                        })
                      }
                    />
                  </div>
                </div>

                <Separator />

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-background px-3 py-2">
                    <Switch
                      id={`policy-allow-${policy.id}`}
                      checked={policy.allowStacking}
                      onCheckedChange={(checked) => handleUpdate({ allowStacking: checked })}
                    />
                    <label
                      htmlFor={`policy-allow-${policy.id}`}
                      className="text-xs font-medium uppercase text-muted-foreground"
                    >
                      Allow stacking
                    </label>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-background px-3 py-2">
                    <Switch
                      id={`policy-approval-${policy.id}`}
                      checked={policy.approvalRequired}
                      onCheckedChange={(checked) => handleUpdate({ approvalRequired: checked })}
                    />
                    <label
                      htmlFor={`policy-approval-${policy.id}`}
                      className="text-xs font-medium uppercase text-muted-foreground"
                    >
                      Requires approval
                    </label>
                  </div>
                </div>

                <ReasonCodesEditor
                  policyIndex={index}
                  items={policy.reasonCodes}
                  onChange={handleReasonCodesChange}
                />

                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">
                    Created {new Date(policy.createdAt).toLocaleString()}
                  </span>
                  <Button
                    variant="ghost"
                    className="text-destructive"
                    onClick={() => onChange(value.filter((_, idx) => idx !== index))}
                  >
                    Remove policy
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      <div>
        <h4 className="text-xs font-semibold uppercase text-muted-foreground">JSON preview</h4>
        <pre className="mt-2 max-h-64 overflow-auto rounded-lg border border-border/60 bg-muted/20 p-4 text-xs text-muted-foreground">
          {preview}
        </pre>
      </div>
    </div>
  );
};
